import { useState, useEffect, useRef } from "react";

import ActionButton from "@/components/Button/ActionButton";
import IconButton from "@/components/Button/IconButton";
import {
  ComboboxOptionRenderer,
  ComboboxOptionComponent,
  ComboboxOption,
} from "@/components/Combobox/types";
import { HStack, VStack } from "@/components/Layout";
import { ControlledSelect } from "@/components/Select/ControlledSelect";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from "../CFormItem";

type Props<TOption extends ComboboxOption> = CFormItemProps & {
  value?: string[];
  onChange: (value: string[]) => void;
  fetchOptions: (text?: string) => Promise<TOption[]>;
  renderOption?:
    | ComboboxOptionRenderer<TOption>
    | ComboboxOptionComponent<TOption>;
  inputClassName?: string;
  hideSearch?: boolean;
  inputWidth?: number | string;
};

/**
 * Card-based Form / SelectItems
 *
 * CFromMultiSelectItemとの使い分け
 * - 選択肢が少ない場合：
 *   - CFromMultiSelectItemを使用する
 * - 選択肢が多い場合：
 *   - CFromSelectItemsを使用する
 */
export const CFormSelectItems = buildCFormItem(
  <TOption extends ComboboxOption>(props: Props<TOption>) => {
    const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const fetchOptionsRef = useRef(props.fetchOptions);

    // 常に最新の fetchOptions を保持
    useEffect(() => {
      fetchOptionsRef.current = props.fetchOptions;
    }, [props.fetchOptions]);

    useEffect(() => {
      const loadLabels = async () => {
        if (!props.value || props.value.length === 0) {
          setSelectedLabels([]);
          return;
        }

        setIsLoading(true);

        try {
          const options = (await fetchOptionsRef.current?.()) ?? [];
          const labels = props.value
            .map(
              (val) =>
                options.find((option: TOption) => option.value === val)?.label,
            )
            .filter((label): label is string => Boolean(label));
          setSelectedLabels(labels);
        } catch (error) {
          console.error("Failed to fetch options:", error);
          setSelectedLabels([]);
        } finally {
          setIsLoading(false);
        }
      };

      loadLabels();
    }, [props.value, props.fetchOptions]);

    const readComponent = (() => {
      if (!props.value || props.value.length === 0) return "-";
      if (isLoading) return "-";
      if (selectedLabels.length === 0) return "-";

      // 複数項目を改行で表示
      return (
        <div className="space-y-1">
          {selectedLabels.map((label) => (
            <div key={label}>{label}</div>
          ))}
        </div>
      );
    })();

    const tc = useCommonTranslation();

    return (
      <CFormItemBase
        {...props}
        readComponent={readComponent}
        writeComponent={
          <VStack className="gap-4 items-start">
            {props.value?.map((value, i) => (
              <HStack key={"selectItems" + i} className="gap-2 items-center">
                <ControlledSelect
                  id={props.id}
                  value={value}
                  onChange={(option) =>
                    props.onChange(
                      props.value?.map((v, index) =>
                        index === i ? (option?.value ?? "") : v,
                      ) ?? [],
                    )
                  }
                  fetchOptions={props.fetchOptions}
                  hideSearch={props.hideSearch}
                  allowEmpty={false}
                  width={props.inputWidth}
                  disabled={props.readonly}
                  renderOption={props.renderOption}
                />
                {(!props.required || i > 0) && (
                  <IconButton
                    icon="Trash2Icon"
                    variant="ghost"
                    onClick={() =>
                      props.onChange(
                        props.value?.filter((_, index) => index !== i) ?? [],
                      )
                    }
                  />
                )}
              </HStack>
            ))}
            <ActionButton
              icon="PlusIcon"
              variant="ghost"
              onClick={() => props.onChange([...(props.value ?? []), ""])}
            >
              {tc("action.add")}
            </ActionButton>
          </VStack>
        }
      />
    );
  },
);
